import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../api/adminApi";
import type { WorkflowActivityOutputField } from "../../../../../api/types/workflowTypes";
import WorkflowTemplateDetailsContext from "../WorkflowTemplateDetailsContext";

interface Props {
  activityId: string;
  field: WorkflowActivityOutputField;
  onClose: () => void;
}

const updateActivityInWorkflowTemplate = withErrorHandling(adminApi.updateActivityInWorkflowTemplate);

const DeleteWorkflowTemplateActivityFieldDialog = ({ activityId, field, onClose }: Props) => {
  const { workflowTemplate, onActivityUpdated } = useDefinedContext(WorkflowTemplateDetailsContext);
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [loading, setLoading] = useState(false);

  const handleDeleteClick = async () => {
    setLoading(true);
    const [updatedActivity, error] = await updateActivityInWorkflowTemplate(workflowTemplate.id, activityId, {
      outputFieldSchema: {
        removedFieldIds: [field.id],
      },
      updateActiveWorkflows: false,
    });
    setLoading(false);

    if (error) {
      logError(error, "[DeleteWorkflowTemplateActivityFieldDialog] updateActivityInWorkflowTemplate");
      sendNotificationError(error.message);
      return;
    }

    onActivityUpdated(updatedActivity);
    sendNotification("Field removed successfully");
    onClose();
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Remove Field</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6">Remove &quot;{field.displayName}&quot;?</Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              This action may affect workflow execution if this field is used in any activity.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="outlined" color="error" loading={loading} onClick={handleDeleteClick}>
          Remove
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteWorkflowTemplateActivityFieldDialog;
